// GitLab-style per-issue activity feed (notes + state/milestone events).
// Separate from `useIssueTimeline` which carries Loom's methodology
// progression view.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  IssueActivityResponseSchema,
  type IssueActivityEvent,
} from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

export interface IssueActivityState {
  readonly events: ReadonlyArray<IssueActivityEvent>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

export const useIssueActivity = (
  projectId: number,
  iid: number | null,
): IssueActivityState => {
  const [events, setEvents] = useState<ReadonlyArray<IssueActivityEvent>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  const cancelled = useRef(false);

  const refetch = useCallback(() => setBump((n) => n + 1), []);

  useEffect(() => {
    cancelled.current = false;
    setEvents([]);
    setError(null);
    if (iid === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    httpGet(
      `/api/projects/${projectId}/issues/${iid}/activity`,
      IssueActivityResponseSchema,
    )
      .then((res) => {
        if (cancelled.current) return;
        setEvents(res.events);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled.current) return;
        if (err instanceof UnauthorizedError) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled.current = true;
    };
  }, [projectId, iid, bump]);

  return { events, loading, error, refetch };
};
